import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, Navigate, useLocation, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { apiUrl } from "./lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 

  Home, Search, User, MessageSquare, Star, MapPin, Clock, Shield, CheckCircle, 
  Phone, ChevronRight, Wrench, Zap, Droplet, Flame, Building2, Paintbrush, 
  Grid3X3, Hammer, RefreshCw, AlertTriangle, Truck, Trash2, UtensilsCrossed,
  Menu, X, Calendar, FileText, Lock, Users, Settings, Briefcase, ClipboardList,
  DollarSign, UserCheck, LogOut, ArrowRight, Sun, Upload, Image as ImageIcon, XCircle,
  UserPlus, Bell, Inbox
} from "lucide-react";

// Configure axios defaults for credentials (important for Render deployment)
axios.defaults.withCredentials = true;

// ============================================================
// API CONFIGURATION - DEPLOYMENT AGNOSTIC
// ============================================================
// All API calls use apiUrl() from ./lib/api.js
// This ensures consistent URL handling across environments
// Local: Uses REACT_APP_BACKEND_URL from .env
// Render: Uses /api (relative) or full backend URL
// ============================================================

// Auth Helper Functions
const getToken = () => localStorage.getItem("token");
const getRole = () => localStorage.getItem("role");
const getUser = () => {
  const userData = localStorage.getItem("werkora_user");
  if (userData) {
    try {
      return JSON.parse(userData);
    } catch (e) {
      // fall through to role fallback
    }
  }
  // Fallback: token + role exist but user blob is missing/corrupted.
  // Build a minimal user object so role-based guards can still route correctly.
  const role = localStorage.getItem("role");
  const token = localStorage.getItem("token");
  if (role && token) {
    return { id: null, role };
  }
  return null;
};

const saveAuth = (user) => {
  localStorage.setItem("token", user.id);
  localStorage.setItem("role", user.role);
  localStorage.setItem("werkora_user", JSON.stringify(user));
};

const clearAuth = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("role");
  localStorage.removeItem("werkora_user");
};

// Service Icons mapping
const SERVICE_ICONS = {
  elektriker: Zap,
  sanitaer: Droplet,
  heizungsbauer: Flame,
  dachdecker: Building2,
  maler: Paintbrush,
  fliesenleger: Grid3X3,
  fliesen_keramik: Grid3X3,
  trockenbau: Building2,
  tischler: Hammer,
  renovierung: RefreshCw,
  schimmel: AlertTriangle,
  bauunternehmen: Building2,
  photovoltaik: Sun,
  fenster_tueren: Home,
  waermedaemmung: Home,
  bodenleger: Grid3X3,
  fassadenbau: Building2,
  regenrinnen: Droplet,
  moebelmontage: Truck,
  entruempelung: Trash2,
  kuechenmontage: UtensilsCrossed
};

// ==================== WERKORA LOGO COMPONENT ====================
const WerkoraLogo = ({ className = "h-10 w-auto" }) => (
  <svg viewBox="0 0 200 50" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M25 8L5 22V42H45V22L25 8Z" stroke="#3B82F6" strokeWidth="2" fill="none"/>
    <path d="M18 42V28H32V42" stroke="#3B82F6" strokeWidth="2" fill="none"/>
    <circle cx="25" cy="18" r="3" fill="#10B981"/>
    <circle cx="12" cy="30" r="2" fill="#10B981"/>
    <circle cx="38" cy="30" r="2" fill="#10B981"/>
    <circle cx="25" cy="38" r="2" fill="#10B981"/>
    <line x1="25" y1="18" x2="12" y2="30" stroke="#10B981" strokeWidth="1" strokeDasharray="2"/>
    <line x1="25" y1="18" x2="38" y2="30" stroke="#10B981" strokeWidth="1" strokeDasharray="2"/>
    <line x1="25" y1="18" x2="25" y2="38" stroke="#10B981" strokeWidth="1" strokeDasharray="2"/>
    <text x="55" y="34" fontFamily="system-ui, -apple-system, sans-serif" fontSize="24" fontWeight="600" fill="#1F2937">Werkora</text>
  </svg>
);

// ==================== AUTH GUARD COMPONENT ====================
import { AuthProvider, useAuth } from "./auth/AuthContext";
import NotificationBell, { NotificationsProvider } from "./components/NotificationBell";
import { Toaster } from "./components/ui/sonner";

// Helper to compute the dashboard path for a given role
const dashboardPathForRole = (role) => {
  if (role === "worker" || role === "provider") return "/handwerker";
  return "/customer";
};

function AuthGuard({ children, allowedRole }) {
  const { user, authLoading } = useAuth();
  const token = getToken();
  const storedUser = getUser();

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  // Use either context user or localStorage user
  const currentUser = user || storedUser;

  // Not logged in
  if (!currentUser && !token) {
    return <Navigate to="/login" replace />;
  }

  // Token exists but we have no user info — force re-login (avoids accidental
  // default redirect to the wrong role dashboard).
  if (!currentUser?.role) {
    return <Navigate to="/login" replace />;
  }

  // Wrong role - redirect to user's correct dashboard
  if (allowedRole && currentUser.role !== allowedRole) {
    return <Navigate to={dashboardPathForRole(currentUser.role)} replace />;
  }

  return children;
}

// Customer-only routes (logged-in customer required)
function CustomerOnlyRoute({ children }) {
  return <AuthGuard allowedRole="customer">{children}</AuthGuard>;
}

// Provider/Handwerker-only routes (logged-in worker required)
function ProviderOnlyRoute({ children }) {
  return <AuthGuard allowedRole="worker">{children}</AuthGuard>;
}

// Routes that should ONLY be visible to guests (not logged-in users).
// Example: /login, /register. Redirect logged-in users to their dashboard.
function GuestOnlyRoute({ children }) {
  const { user, authLoading } = useAuth();
  const storedUser = getUser();
  const token = getToken();

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  const currentUser = user || storedUser;
  if (currentUser && token) {
    return <Navigate to={dashboardPathForRole(currentUser.role)} replace />;
  }
  return children;
}

// Routes accessible to GUESTS or CUSTOMERS only.
// Logged-in providers/workers must NOT see these (e.g. /request-service).
function GuestOrCustomerRoute({ children }) {
  const { user, authLoading } = useAuth();
  const storedUser = getUser();

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  const currentUser = user || storedUser;
  if (currentUser && (currentUser.role === "worker" || currentUser.role === "provider")) {
    // Provider should not access customer-side flows
    return <Navigate to="/handwerker" replace />;
  }
  return children;
}

// Routes accessible to GUESTS only for provider registration.
// Logged-in users (any role) should be redirected to their dashboard.
function GuestOrProviderRoute({ children }) {
  const { user, authLoading } = useAuth();
  const storedUser = getUser();

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  const currentUser = user || storedUser;
  if (currentUser) {
    return <Navigate to={dashboardPathForRole(currentUser.role)} replace />;
  }
  return children;
}

// ==================== STAR RATING COMPONENT ====================
const StarRating = ({ rating, onRate, readonly = false, size = "md" }) => {
  const [hovered, setHovered] = useState(0);
  
  const sizes = {
    sm: "h-4 w-4",
    md: "h-6 w-6",
    lg: "h-8 w-8"
  };
  
  const sizeClass = sizes[size] || sizes.md;
  
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          className={`transition-colors ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
          onClick={() => !readonly && onRate && onRate(star)}
          onMouseEnter={() => !readonly && setHovered(star)}
          onMouseLeave={() => !readonly && setHovered(0)}
        >
          <Star
            className={`${sizeClass} ${
              star <= (hovered || rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// ==================== RATING MODAL COMPONENT ====================
const RatingModal = ({ isOpen, onClose, order, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  
  if (!isOpen || !order) return null;
  
  const handleSubmit = async () => {
    if (rating === 0) {
      setError("Bitte wählen Sie eine Bewertung");
      return;
    }
    
    setSubmitting(true);
    setError("");
    
    try {
      await onSubmit({ rating, comment });
      onClose();
    } catch (err) {
      setError(err.message || "Fehler beim Speichern der Bewertung");
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500" />
            Handwerker bewerten
          </CardTitle>
          <CardDescription>
            Wie zufrieden waren Sie mit dem Service?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          <div className="text-center py-4">
            <StarRating rating={rating} onRate={setRating} size="lg" />
            <p className="text-sm text-gray-500 mt-2">
              {rating === 0 && "Klicken Sie auf die Sterne"}
              {rating === 1 && "Sehr schlecht"}
              {rating === 2 && "Schlecht"}
              {rating === 3 && "Okay"}
              {rating === 4 && "Gut"}
              {rating === 5 && "Ausgezeichnet"}
            </p>
          </div>
          
          <div>
            <Label>Kommentar (optional)</Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Beschreiben Sie Ihre Erfahrung..."
              className="mt-1"
              rows={3}
            />
          </div>
        </CardContent>
        <CardFooter className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Abbrechen
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting || rating === 0}
            className="flex-1 bg-yellow-500 hover:bg-yellow-600"
          >
            {submitting ? "Speichere..." : "Bewertung abgeben"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

// ==================== REVIEWS LIST COMPONENT ====================
const ReviewsList = ({ providerId }) => {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const [reviewsRes, summaryRes] = await Promise.all([
          axios.get(apiUrl(`/providers/${providerId}/reviews`)),
          axios.get(apiUrl(`/providers/${providerId}/rating-summary`))
        ]);
        setReviews(reviewsRes.data.reviews || []);
        setSummary(summaryRes.data);
      } catch (error) {
        console.error("Error fetching reviews:", error);
      } finally {
        setLoading(false);
      }
    };
    
    if (providerId) {
      fetchReviews();
    }
  }, [providerId]);
  
  if (loading) {
    return <div className="text-center py-4 text-gray-500">Laden...</div>;
  }
  
  return (
    <div className="space-y-4">
      {/* Rating Summary */}
      {summary && (
        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900">
              {summary.average_rating || 0}
            </div>
            <StarRating rating={Math.round(summary.average_rating || 0)} readonly size="sm" />
            <p className="text-sm text-gray-500 mt-1">
              {summary.review_count || 0} Bewertungen
            </p>
          </div>
          
          {/* Rating Breakdown */}
          <div className="flex-1 space-y-1">
            {[5, 4, 3, 2, 1].map((stars) => {
              const count = summary.breakdown?.[stars] || 0;
              const percentage = summary.review_count > 0 
                ? (count / summary.review_count) * 100 
                : 0;
              return (
                <div key={stars} className="flex items-center gap-2 text-sm">
                  <span className="w-3">{stars}</span>
                  <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-400 rounded-full"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="w-8 text-gray-500">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Reviews List */}
      {reviews.length === 0 ? (
        <p className="text-center text-gray-500 py-4">Noch keine Bewertungen</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((review) => (
            <div key={review.id} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback>{review.customer_name?.charAt(0) || "K"}</AvatarFallback>
                  </Avatar>
                  <span className="font-medium text-sm">{review.customer_name || "Kunde"}</span>
                </div>
                <StarRating rating={review.rating} readonly size="sm" />
              </div>
              {review.comment && (
                <p className="text-sm text-gray-600">{review.comment}</p>
              )}
              <p className="text-xs text-gray-400 mt-2">
                {new Date(review.created_at).toLocaleDateString('de-DE')}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ==================== HEADER COMPONENT ====================
const Header = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const token = getToken();
  const role = getRole();

  const handleLogout = () => {
    clearAuth();
    window.location.href = "/";
  };

  const getDashboardPath = () => {
    return role === "customer" ? "/customer" : "/handwerker";
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center cursor-pointer" onClick={() => navigate("/")} data-testid="logo">
            <WerkoraLogo className="h-10 w-auto" />
          </div>
          
          <nav className="hidden md:flex items-center space-x-6">
            <button onClick={() => navigate("/")} className="text-gray-600 hover:text-gray-900 font-medium">
              Startseite
            </button>
            <button onClick={() => navigate("/services")} className="text-gray-600 hover:text-gray-900 font-medium">
              Services
            </button>
            {token ? (
              <>
                <button onClick={() => navigate(getDashboardPath())} className="text-gray-600 hover:text-gray-900 font-medium">
                  Dashboard
                </button>
                <NotificationBell />
                <Button variant="outline" onClick={handleLogout}>
                  Abmelden
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => navigate("/login")}>
                  Anmelden
                </Button>
                <Button onClick={() => navigate("/register")} className="bg-blue-600 hover:bg-blue-700">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Registrieren
                </Button>
              </>
            )}
          </nav>

          <div className="md:hidden flex items-center gap-1">
            {token && <NotificationBell />}
            <button className="p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100">
            <div className="flex flex-col space-y-3">
              <button onClick={() => { navigate("/"); setMobileMenuOpen(false); }} className="text-gray-600 hover:text-gray-900 font-medium py-2">
                Startseite
              </button>
              <button onClick={() => { navigate("/services"); setMobileMenuOpen(false); }} className="text-gray-600 hover:text-gray-900 font-medium py-2">
                Services
              </button>
              {token ? (
                <>
                  <button onClick={() => { navigate(getDashboardPath()); setMobileMenuOpen(false); }} className="text-gray-600 hover:text-gray-900 font-medium py-2">
                    Dashboard
                  </button>
                  <Button variant="outline" onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>
                    Abmelden
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => { navigate("/login"); setMobileMenuOpen(false); }}>
                    Anmelden
                  </Button>
                  <Button onClick={() => { navigate("/register"); setMobileMenuOpen(false); }} className="bg-blue-600 hover:bg-blue-700">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Registrieren
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// ==================== FOOTER COMPONENT ====================
const Footer = () => {
  const navigate = useNavigate();
  
  return (
    <footer className="bg-gray-50 border-t border-gray-200 mt-auto" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <WerkoraLogo className="h-8 w-auto mb-4" />
            <p className="text-gray-600 text-sm">
              Services rund um Haus, Renovierung & Umzug
            </p>
            <Badge variant="outline" className="mt-3 text-blue-600 border-blue-200">
              <MapPin className="h-3 w-3 mr-1" /> Pilot-Region: Hamburg & Schleswig-Holstein
            </Badge>
          </div>
          
          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Services</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li><button onClick={() => navigate("/services")} className="hover:text-blue-600">Alle Services</button></li>
              <li><button onClick={() => navigate("/login")} className="hover:text-blue-600">Service anfragen</button></li>
              <li><button onClick={() => navigate("/services")} className="hover:text-blue-600">Anbieter finden</button></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Für Anbieter</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li><button onClick={() => navigate("/login")} className="hover:text-blue-600">Als Anbieter registrieren</button></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Rechtliches</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li><span className="hover:text-blue-600 cursor-pointer">Datenschutz</span></li>
              <li><span className="hover:text-blue-600 cursor-pointer">Impressum</span></li>
            </ul>
          </div>
        </div>
        
        <Separator className="my-8" />
        
        <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
          <p>© 2026 Werkora. Alle Rechte vorbehalten.</p>
          <div className="flex items-center mt-4 md:mt-0">
            <Shield className="h-4 w-4 mr-1 text-green-600" />
            <span>DSGVO-konform</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ==================== HOME PAGE ====================
// ==================== HOME PAGE (MOBILE-FIRST APP DESIGN) ====================
const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchMessage, setSearchMessage] = useState("");
  const [categories, setCategories] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = getToken();

  useEffect(() => {
    const fetchCategories = async () => {
      setError(null);
      try {console.log("Fetching categories from:", apiUrl("/services/categories"));
const response = await axios.get(apiUrl("/services/categories"));

        console.log("Categories response:", response.data);
        setCategories(response.data);
      } catch (err) {
        console.error("Error fetching categories:", err);
        setError("Fehler beim Laden der Services. Bitte laden Sie die Seite neu.");
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length >= 2) {
      try {
        const response = await axios.get(apiUrl(`/services/search?keyword=${encodeURIComponent(query)}`));
        setSearchResults(response.data.results);
        if (response.data.matched_service) {
          setSearchMessage(response.data.message);
        }
      } catch (err) {
        console.error("Search error:", err);
      }
    } else {
      setSearchResults([]);
      setSearchMessage("");
    }
  };

  const handleServiceSelect = (serviceId) => {
    navigate(`/request-service?category=${serviceId}`);
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0" data-testid="home-page">
      {/* Beta "Free" Banner — full width, sticky-feeling top strip */}
      <div
        className="bg-gradient-to-r from-emerald-500 via-emerald-500 to-green-500 text-white text-center text-sm md:text-base font-medium py-2.5 px-3 shadow-sm"
        data-testid="beta-free-banner"
      >
        <span className="inline-flex items-center gap-2">
          <CheckCircle className="h-4 w-4" />
          Kostenlos testen – keine Gebühren in der Beta
          <span className="hidden sm:inline">· 100% kostenlos</span>
        </span>
      </div>

      {/* Mobile App Header */}
      <div className="md:hidden sticky top-16 z-40 bg-white border-b px-4 py-3">
        <div className="flex items-center justify-center">
          <div className="flex items-center px-3 py-1.5 bg-blue-50 rounded-full text-sm">
            <MapPin className="h-3.5 w-3.5 text-blue-600 mr-1.5" />
            <span className="text-blue-700 font-medium">Hamburg & Schleswig-Holstein</span>
          </div>
        </div>
      </div>

      {/* Hero Section - Mobile First */}
      <section className="bg-white px-4 pt-6 pb-8 md:py-16 lg:py-24 md:bg-gradient-to-br md:from-blue-50 md:via-white md:to-green-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            {/* Desktop Badge */}
            <Badge variant="outline" className="hidden md:inline-flex mb-6 text-blue-600 border-blue-200 px-4 py-1">
              <MapPin className="h-3 w-3 mr-1" /> Pilot-Region: Hamburg & Schleswig-Holstein
            </Badge>
            
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6" data-testid="hero-title">
              Der richtige Handwerker für Ihr Zuhause
            </h1>
            <p className="text-base md:text-xl text-gray-600 mb-6 md:mb-8 px-2">
              Finden Sie geprüfte Fachleute für Renovierung, Reparatur, Installation und Umzug – schnell, sicher und transparent.
            </p>
            
            {/* Search Box */}
            <div className="relative max-w-xl mx-auto mb-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Was brauchen Sie? z.B. Elektriker, Sanitär, Küche…"
                  className="pl-12 h-14 text-base md:text-lg border-gray-200 focus:border-blue-500 rounded-xl"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  data-testid="search-input"
                />
              </div>
              
              {/* Search Results Dropdown */}
              {searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                  <div className="p-2 text-sm text-blue-600 border-b font-medium bg-blue-50">
                    {searchMessage || "Diese Anbieter passen zu Ihrer Anfrage"}
                  </div>
                  {searchResults.map((result) => {
                    const Icon = SERVICE_ICONS[result.id] || Wrench;
                    return (
                      <button
                        key={result.id}
                        onClick={() => handleServiceSelect(result.id)}
                        className="w-full flex items-center px-4 py-3 hover:bg-gray-50 text-left"
                      >
                        <Icon className="h-5 w-5 text-blue-600 mr-3" />
                        <div>
                          <div className="font-medium text-gray-900">{result.name}</div>
                          <div className="text-sm text-gray-500">{result.group}</div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-400 ml-auto" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* CTA Buttons - Primary (blue) dominant, Secondary (green) subdued */}
            <div className="flex flex-col gap-[10px] max-w-md mx-auto px-4">
              <button
                onClick={() => navigate("/request-service")}
                data-testid="request-service-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white shadow-md hover:shadow-lg transition-all"
                style={{
                  height: "54px",
                  fontSize: "16px",
                  fontWeight: 600,
                  borderRadius: "14px",
                  padding: "0 16px",
                }}
              >
                <FileText className="h-5 w-5" />
                Service anfragen
              </button>

              <button
                onClick={() => navigate("/provider/register")}
                data-testid="provider-register-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600/95 hover:bg-emerald-600 active:bg-emerald-700 text-white transition-colors"
                style={{
                  height: "46px",
                  fontSize: "15px",
                  fontWeight: 500,
                  borderRadius: "12px",
                  padding: "0 16px",
                  opacity: 0.95,
                }}
              >
                <Wrench className="h-5 w-5" />
                Als Handwerker registrieren
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-4 md:py-8 bg-gray-50 border-y">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8 text-xs md:text-sm text-gray-600">
            <div className="flex items-center">
              <Shield className="h-4 w-4 md:h-5 md:w-5 text-green-600 mr-1.5" />
              <span>DSGVO-konform</span>
            </div>
            <div className="flex items-center">
              <Lock className="h-4 w-4 md:h-5 md:w-5 text-blue-600 mr-1.5" />
              <span>Verschlüsselte Daten</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-green-600 mr-1.5" />
              <span>Verifizierte Anbieter</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - App Style Cards */}
      <section className="py-8 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-6 md:mb-12">
            <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-4">
              Services
            </h2>
            <p className="text-sm md:text-base text-gray-600">
              Wählen Sie den passenden Service
            </p>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
              <p className="text-gray-600 text-sm">Laden...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
              <p className="text-gray-600 mb-4 text-sm">{error}</p>
              <Button onClick={() => window.location.reload()} variant="outline" size="sm">
                Seite neu laden
              </Button>
            </div>
          ) : categories && categories.categories && (
            <div className="space-y-8 md:space-y-12">
              {Object.entries(categories.categories).map(([groupKey, group]) => (
                <div key={groupKey}>
                  <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-4 md:mb-6">{group.name_de}</h3>
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                    {group.services.map((service) => {
                      const Icon = SERVICE_ICONS[service.id] || Wrench;
                      return (
                        <button 
                          key={service.id} 
                          className="flex flex-col items-center p-3 md:p-4 bg-white border border-gray-100 rounded-xl hover:shadow-md hover:border-blue-200 transition-all active:scale-95"
                          onClick={() => handleServiceSelect(service.id)}
                        >
                          <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-50 rounded-full flex items-center justify-center mb-2">
                            <Icon className="h-5 w-5 md:h-6 md:w-6 text-blue-600" />
                          </div>
                          <span className="font-medium text-gray-900 text-xs md:text-sm text-center leading-tight">
                            {service.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How It Works - Mobile Optimized */}
      <section className="py-8 md:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-6 md:mb-12">
            <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-2">So funktioniert's</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {[
              { icon: Search, title: "1. Service wählen", desc: "Passenden Service auswählen" },
              { icon: FileText, title: "2. Beschreiben", desc: "Problem beschreiben" },
              { icon: Users, title: "3. Anbieter", desc: "Passende Anbieter erhalten" },
              { icon: MessageSquare, title: "4. Kontakt", desc: "Termin vereinbaren" }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <step.icon className="h-5 w-5 md:h-8 md:w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 text-sm md:text-base mb-1">{step.title}</h3>
                <p className="text-gray-600 text-xs md:text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA for Providers */}
      <section className="py-10 md:py-16 bg-gradient-to-r from-green-600 to-green-700">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-xl md:text-3xl font-bold text-white mb-3 md:mb-4">Sind Sie Handwerker?</h2>
          <p className="text-green-100 mb-6 max-w-2xl mx-auto text-sm md:text-base">
            Mit Werkora erhalten Sie gezielt passende Anfragen für Ihre Leistungen.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-green-700 hover:bg-green-50 h-12 px-8"
            onClick={() => navigate("/provider/register")}
          >
            Als Handwerker registrieren <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

// ==================== SERVICES PAGE ====================
const ServicesPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = getToken();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(apiUrl("/services/categories"));
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleServiceSelect = (serviceId) => {
    if (token) {
      navigate(`/customer?service=${serviceId}`);
    } else {
      navigate("/login");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Laden...</div>;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {categories?.title || "Services rund um Haus, Renovierung & Umzug"}
          </h1>
          <p className="text-gray-600">Wählen Sie den passenden Service für Ihr Anliegen</p>
        </div>

        {categories && Object.entries(categories.categories).map(([groupKey, group]) => (
          <div key={groupKey} className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">{group.name_de}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {group.services.map((service) => {
                const Icon = SERVICE_ICONS[service.id] || Wrench;
                return (
                  <Card 
                    key={service.id}
                    className="cursor-pointer hover:shadow-lg transition-shadow hover:border-blue-200"
                    onClick={() => handleServiceSelect(service.id)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                          <Icon className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1">{service.name}</h3>
                          <p className="text-sm text-gray-500">Klicken für Service-Anfrage</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== LOGIN PAGE ====================
const LoginPage = () => {
  const [step, setStep] = useState("role"); // "role" | "email" | "code"
  const [selectedRole, setSelectedRole] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  // Cooldown tick
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const id = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // Check if already logged in
  useEffect(() => {
    const token = getToken();
    const role = getRole();
    if (token && role) {
      const redirectPath = role === "customer" ? "/customer" : "/handwerker";
      window.location.href = redirectPath;
    }
  }, []);

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setStep("email");
    setError("");
  };

  const handleSendCode = async () => {
    if (!email || !email.includes("@")) {
      setError("Bitte geben Sie eine gültige E-Mail-Adresse ein");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      await axios.post(apiUrl("/auth/send-code-email"), { email: email.trim().toLowerCase() });
      setStep("code");
      setResendCooldown(60);
    } catch (err) {
      // Parse structured error from backend
      const detail = err?.response?.data?.detail;
      const detailObj = typeof detail === "object" ? detail : null;
      if (detailObj?.code === "rate_limited" && typeof detailObj.retry_after_seconds === "number") {
        setResendCooldown(detailObj.retry_after_seconds);
      }
      const message = detailObj?.message
        || (typeof detail === "string" ? detail : null)
        || (err.request ? "Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung." : "Fehler beim Senden des Codes");
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (code.length !== 6) {
      setError("Bitte geben Sie den 6-stelligen Code ein");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(apiUrl("/auth/verify-code-email"), {
        email: email.trim().toLowerCase(),
        code: code,
        role: selectedRole
      });
      
      if (response.data.ok && response.data.token) {
        const user = response.data.user;
        const token = response.data.token;
        
        // Check if existing user has different role
        if (!response.data.is_new_user && user.role !== selectedRole) {
          setError(`Dieses Konto ist bereits als ${user.role === 'customer' ? 'Kunde' : 'Handwerker'} registriert`);
          return;
        }
        
        // Save token and auth data to localStorage
        localStorage.setItem("token", token);
        localStorage.setItem("role", user.role);
        localStorage.setItem("werkora_user", JSON.stringify(user));
        
        // Redirect to appropriate dashboard
        const redirectPath = user.role === "customer" ? "/customer" : "/handwerker";
        window.location.href = redirectPath;
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const detailObj = typeof detail === "object" ? detail : null;
      const errCode = detailObj?.code;

      let message;
      if (errCode === "expired") {
        message = "Der Code ist abgelaufen. Bitte fordern Sie einen neuen Code an.";
      } else if (errCode === "invalid") {
        message = "Der Bestätigungscode ist ungültig.";
      } else if (errCode === "too_many_tries") {
        message = "Zu viele fehlgeschlagene Versuche. Bitte fordern Sie einen neuen Code an.";
      } else if (errCode === "not_found") {
        message = "Kein Code für diese E-Mail gefunden. Bitte fordern Sie einen neuen Code an.";
      } else if (detailObj?.message) {
        message = detailObj.message;
      } else if (typeof detail === "string") {
        message = detail;
      } else if (err.request) {
        message = "Keine Verbindung zum Server";
      } else {
        message = "Fehler bei der Verifizierung";
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === "code") {
      setStep("email");
      setCode("");
    } else if (step === "email") {
      setStep("role");
      setEmail("");
    }
    setError("");
  };

  // Role Selection Step
  if (step === "role") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-10">
            <WerkoraLogo className="h-14 w-auto mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Willkommen bei Werkora</h1>
            <p className="text-lg text-gray-600">Wählen Sie Ihre Rolle</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <button
              onClick={() => handleRoleSelect("customer")}
              className="group bg-white border-2 border-gray-200 hover:border-blue-500 rounded-2xl p-8 transition-all duration-200 hover:shadow-xl text-left"
              data-testid="role-customer-btn"
            >
              <div className="w-20 h-20 bg-blue-100 group-hover:bg-blue-200 rounded-full flex items-center justify-center mb-6 mx-auto transition-colors">
                <User className="h-10 w-10 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">Ich bin Kunde</h2>
              <p className="text-gray-600 text-center mb-4">
                Suchen Sie einen Handwerker für Ihr Zuhause?
              </p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" /> Service anfragen</li>
                <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" /> Handwerker durchsuchen</li>
                <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" /> Aufträge verfolgen</li>
              </ul>
            </button>

            <button
              onClick={() => handleRoleSelect("worker")}
              className="group bg-white border-2 border-gray-200 hover:border-green-500 rounded-2xl p-8 transition-all duration-200 hover:shadow-xl text-left"
              data-testid="role-worker-btn"
            >
              <div className="w-20 h-20 bg-green-100 group-hover:bg-green-200 rounded-full flex items-center justify-center mb-6 mx-auto transition-colors">
                <Wrench className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">Ich bin Handwerker</h2>
              <p className="text-gray-600 text-center mb-4">
                Möchten Sie neue Aufträge erhalten?
              </p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" /> Anfragen erhalten</li>
                <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" /> Services verwalten</li>
                <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" /> Preise festlegen</li>
              </ul>
            </button>
          </div>

          <p className="text-center text-sm text-gray-500 mt-8">
            <Lock className="h-4 w-4 inline mr-1" />
            Ihre Rolle wird dauerhaft mit Ihrem Konto verknüpft
          </p>

          <div className="mt-4 text-center">
            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="login-free-badge">
              <CheckCircle className="h-3 w-3 mr-1" /> 100% kostenlos in der Beta
            </Badge>
          </div>
        </div>
      </div>
    );
  }

  // Email/Code Entry Step
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <WerkoraLogo className="h-12 w-auto mx-auto mb-4" />
          <div className="flex justify-center mb-4">
            <Badge className={selectedRole === "customer" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}>
              {selectedRole === "customer" ? (
                <><User className="h-3 w-3 mr-1" /> Kunde</>
              ) : (
                <><Wrench className="h-3 w-3 mr-1" /> Handwerker</>
              )}
            </Badge>
          </div>
          <CardTitle>{step === "email" ? "E-Mail-Anmeldung" : "Code eingeben"}</CardTitle>
          <CardDescription>
            {step === "email" 
              ? "Geben Sie Ihre E-Mail-Adresse ein" 
              : "Geben Sie den 6-stelligen Code ein"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm" data-testid="error-message">
              {error}
            </div>
          )}

          {step === "email" && (
            <div>
              <Label>E-Mail-Adresse</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ihre@email.de"
                className="mt-1"
                data-testid="email-input"
              />
            </div>
          )}

          {step === "code" && (
            <div>
              <Label>Bestätigungscode</Label>
              <Input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-stelliger Code"
                className="mt-1 text-center text-xl tracking-widest"
                maxLength={6}
                data-testid="code-input"
                inputMode="numeric"
                autoComplete="one-time-code"
              />
              <p className="text-sm text-gray-500 mt-3 text-center">
                Der Bestätigungscode wurde an <strong className="text-gray-700">{email}</strong> gesendet.
                <br />
                <span className="text-xs text-gray-400">
                  Bitte überprüfen Sie auch Ihren Spam-Ordner.
                </span>
              </p>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          {step === "email" ? (
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg"
              onClick={handleSendCode}
              disabled={!email || !email.includes("@") || loading}
              data-testid="send-code-btn"
            >
              {loading ? "Sende..." : "Code senden"}
            </Button>
          ) : (
            <>
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg"
                onClick={handleVerifyCode}
                disabled={code.length !== 6 || loading}
                data-testid="verify-btn"
              >
                {loading ? "Prüfe..." : "Anmelden"}
              </Button>

              <button
                type="button"
                onClick={handleSendCode}
                disabled={resendCooldown > 0 || loading}
                className="w-full text-sm text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
                data-testid="resend-code-btn"
              >
                {resendCooldown > 0
                  ? `Neuen Code anfordern (in ${resendCooldown}s)`
                  : "Neuen Code anfordern"}
              </button>
            </>
          )}
          
          <Button variant="ghost" onClick={handleBack} className="w-full">
            <ChevronRight className="h-4 w-4 mr-2 rotate-180" /> Zurück
          </Button>

          <p className="text-center text-sm text-gray-500 mt-2">
            Noch kein Konto? <Link to="/register" className="text-blue-600 hover:underline">Jetzt registrieren</Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

// ==================== REGISTER PAGE (EMAIL-BASED) ====================
const RegisterPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("role"); // "role" | "form" | "code"
  const [selectedRole, setSelectedRole] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const id = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // Check if already logged in
  useEffect(() => {
    const token = getToken();
    const role = getRole();
    if (token && role) {
      const redirectPath = role === "customer" ? "/customer" : "/handwerker";
      window.location.href = redirectPath;
    }
  }, []);

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setStep("form");
    setError("");
  };

  const handleSendCode = async () => {
    if (!name || name.length < 2) {
      setError("Bitte geben Sie Ihren Namen ein");
      return;
    }
    if (!email || !email.includes("@")) {
      setError("Bitte geben Sie eine gültige E-Mail-Adresse ein");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      await axios.post(apiUrl("/auth/send-code-email"), { 
        email: email.trim().toLowerCase() 
      });
      setStep("code");
      setResendCooldown(60);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const detailObj = typeof detail === "object" ? detail : null;
      if (detailObj?.code === "rate_limited" && typeof detailObj.retry_after_seconds === "number") {
        setResendCooldown(detailObj.retry_after_seconds);
      }
      const message = detailObj?.message
        || (typeof detail === "string" ? detail : null)
        || (err.request ? "Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung." : "Fehler beim Senden des Codes");
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (code.length !== 6) {
      setError("Bitte geben Sie den 6-stelligen Code ein");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(apiUrl("/auth/verify-code-email"), {
        email: email.trim().toLowerCase(),
        code: code,
        role: selectedRole,
        name: name
      });
      
      if (response.data.ok && response.data.token) {
        const user = response.data.user;
        const token = response.data.token;
        
        // Check if existing user has different role
        if (!response.data.is_new_user && user.role !== selectedRole) {
          setError(`Diese E-Mail ist bereits als ${user.role === 'customer' ? 'Kunde' : 'Handwerker'} registriert. Bitte melden Sie sich an.`);
          return;
        }
        
        // Save token and auth data to localStorage
        localStorage.setItem("token", token);
        localStorage.setItem("role", user.role);
        localStorage.setItem("werkora_user", JSON.stringify(user));
        
        // Redirect to appropriate dashboard
        const redirectPath = user.role === "customer" ? "/customer" : "/handwerker";
        window.location.href = redirectPath;
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const detailObj = typeof detail === "object" ? detail : null;
      const errCode = detailObj?.code;

      let message;
      if (errCode === "expired") {
        message = "Der Code ist abgelaufen. Bitte fordern Sie einen neuen Code an.";
      } else if (errCode === "invalid") {
        message = "Der Bestätigungscode ist ungültig.";
      } else if (errCode === "too_many_tries") {
        message = "Zu viele fehlgeschlagene Versuche. Bitte fordern Sie einen neuen Code an.";
      } else if (errCode === "not_found") {
        message = "Kein Code für diese E-Mail gefunden. Bitte fordern Sie einen neuen Code an.";
      } else if (detailObj?.message) {
        message = detailObj.message;
      } else if (typeof detail === "string") {
        message = detail;
      } else if (err.request) {
        message = "Keine Verbindung zum Server";
      } else {
        message = "Fehler bei der Verifizierung";
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === "code") {
      setStep("form");
      setCode("");
    } else if (step === "form") {
      setStep("role");
      setName("");
      setEmail("");
    }
    setError("");
  };

  // Step 1: Role Selection
  if (step === "role") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-10">
            <WerkoraLogo className="h-14 w-auto mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Konto erstellen</h1>
            <p className="text-lg text-gray-600">Wählen Sie Ihre Rolle für die Registrierung</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <button
              onClick={() => handleRoleSelect("customer")}
              className="group bg-white border-2 border-gray-200 hover:border-blue-500 rounded-2xl p-8 transition-all duration-200 hover:shadow-xl text-left"
              data-testid="register-customer-btn"
            >
              <div className="w-20 h-20 bg-blue-100 group-hover:bg-blue-200 rounded-full flex items-center justify-center mb-6 mx-auto transition-colors">
                <User className="h-10 w-10 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">Als Kunde registrieren</h2>
              <p className="text-gray-600 text-center mb-4">
                Suchen Sie einen Handwerker für Ihr Zuhause?
              </p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" /> Service anfragen</li>
                <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" /> Handwerker durchsuchen</li>
                <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" /> Aufträge verfolgen</li>
              </ul>
            </button>

            <button
              onClick={() => handleRoleSelect("worker")}
              className="group bg-white border-2 border-gray-200 hover:border-green-500 rounded-2xl p-8 transition-all duration-200 hover:shadow-xl text-left"
              data-testid="register-worker-btn"
            >
              <div className="w-20 h-20 bg-green-100 group-hover:bg-green-200 rounded-full flex items-center justify-center mb-6 mx-auto transition-colors">
                <Wrench className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">Als Handwerker registrieren</h2>
              <p className="text-gray-600 text-center mb-4">
                Möchten Sie neue Aufträge erhalten?
              </p>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" /> Anfragen erhalten</li>
                <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" /> Services verwalten</li>
                <li className="flex items-center"><CheckCircle className="h-4 w-4 text-green-500 mr-2" /> Preise festlegen</li>
              </ul>
            </button>
          </div>

          <p className="text-center text-sm text-gray-500 mt-8">
            Bereits registriert? <Link to="/login" className="text-blue-600 hover:underline">Jetzt anmelden</Link>
          </p>

          <div className="mt-4 text-center">
            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="register-free-badge">
              <CheckCircle className="h-3 w-3 mr-1" /> 100% kostenlos in der Beta
            </Badge>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Registration Form (Name + Email)
  if (step === "form") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <WerkoraLogo className="h-12 w-auto mx-auto mb-4" />
            <div className="flex justify-center mb-4">
              <Badge className={selectedRole === "customer" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}>
                {selectedRole === "customer" ? (
                  <><User className="h-3 w-3 mr-1" /> Kunde</>
                ) : (
                  <><Wrench className="h-3 w-3 mr-1" /> Handwerker</>
                )}
              </Badge>
            </div>
            <CardTitle>Registrierung</CardTitle>
            <CardDescription>Geben Sie Ihre Daten ein</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <Label>Vollständiger Name *</Label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Max Mustermann"
                className="mt-1"
                data-testid="register-name-input"
              />
            </div>

            <div>
              <Label>E-Mail-Adresse *</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ihre@email.de"
                className="mt-1"
                data-testid="register-email-input"
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg"
              onClick={handleSendCode}
              disabled={!name || !email || !email.includes("@") || loading}
              data-testid="register-send-code-btn"
            >
              {loading ? "Sende..." : "Code senden"}
            </Button>
            
            <Button variant="ghost" onClick={handleBack} className="w-full">
              <ChevronRight className="h-4 w-4 mr-2 rotate-180" /> Zurück
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Step 3: Code Verification
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <WerkoraLogo className="h-12 w-auto mx-auto mb-4" />
          <div className="flex justify-center mb-4">
            <Badge className={selectedRole === "customer" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}>
              {selectedRole === "customer" ? (
                <><User className="h-3 w-3 mr-1" /> Kunde</>
              ) : (
                <><Wrench className="h-3 w-3 mr-1" /> Handwerker</>
              )}
            </Badge>
          </div>
          <CardTitle>Code eingeben</CardTitle>
          <CardDescription>Geben Sie den 6-stelligen Bestätigungscode ein</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <Label>Bestätigungscode</Label>
            <Input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-stelliger Code"
              className="mt-1 text-center text-xl tracking-widest"
              maxLength={6}
              data-testid="register-code-input"
              inputMode="numeric"
              autoComplete="one-time-code"
            />
            <p className="text-sm text-gray-500 mt-3 text-center">
              Der Bestätigungscode wurde an <strong className="text-gray-700">{email}</strong> gesendet.
              <br />
              <span className="text-xs text-gray-400">Bitte überprüfen Sie auch Ihren Spam-Ordner.</span>
            </p>
          </div>

          <div className="text-sm text-gray-500 text-center">
            Registrierung für: <strong>{name}</strong>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <Button 
            className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg"
            onClick={handleVerifyCode}
            disabled={code.length !== 6 || loading}
            data-testid="register-verify-btn"
          >
            {loading ? "Prüfe..." : "Registrierung abschließen"}
          </Button>

          <button
            type="button"
            onClick={handleSendCode}
            disabled={resendCooldown > 0 || loading}
            className="w-full text-sm text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
            data-testid="register-resend-code-btn"
          >
            {resendCooldown > 0
              ? `Neuen Code anfordern (in ${resendCooldown}s)`
              : "Neuen Code anfordern"}
          </button>
          
          <Button variant="ghost" onClick={handleBack} className="w-full">
            <ChevronRight className="h-4 w-4 mr-2 rotate-180" /> Zurück
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

// ==================== CUSTOMER DASHBOARD ====================
const CustomerDashboard = () => {
  const [searchParams] = useSearchParams();
  const preselectedService = searchParams.get("service");
  
  const [activeTab, setActiveTab] = useState("request");
  const [categories, setCategories] = useState(null);
  const [cities, setCities] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedService, setSelectedService] = useState(preselectedService || "");
  const [city, setCity] = useState("");
  const [description, setDescription] = useState("");
  const [urgency, setUrgency] = useState("normal");
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // NEW: Issue photos state
  const [issuePhotos, setIssuePhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [photoError, setPhotoError] = useState("");
  
  // Rating modal state
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [selectedOrderForRating, setSelectedOrderForRating] = useState(null);
  
  // NEW: Appointment date/time state
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");

  const user = getUser();
  
  // Get minimum date (today) for date picker
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  
  // Get minimum time if date is today
  const getMinTime = () => {
    if (appointmentDate === getMinDate()) {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    return "";
  };
  
  // Handle photo upload
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    
    if (issuePhotos.length + files.length > 5) {
      setPhotoError("Maximum 5 Bilder erlaubt");
      return;
    }
    
    setPhotoError("");
    setUploadingPhotos(true);
    
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        setPhotoError("Nur JPG, PNG, WebP erlaubt");
        return false;
      }
      if (file.size > maxSize) {
        setPhotoError("Maximum 5 MB pro Bild");
        return false;
      }
      return true;
    });
    
    try {
      const formData = new FormData();
      validFiles.forEach(file => formData.append("files", file));
      
      const response = await axios.post(apiUrl("/upload/images"), formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (response.data.uploaded) {
        const newPhotos = response.data.uploaded.map(p => ({
          id: p.id,
          filename: p.filename,
          preview: URL.createObjectURL(validFiles.find(f => f.name === p.filename) || validFiles[0])
        }));
        setIssuePhotos(prev => [...prev, ...newPhotos]);
      }
    } catch (error) {
      console.error("Upload error:", error);
      setPhotoError("Fehler beim Hochladen");
    } finally {
      setUploadingPhotos(false);
    }
  };
  
  // Remove a photo
  const removePhoto = async (photoId) => {
    try {
      await axios.delete(apiUrl(`/images/${photoId}`));
      setIssuePhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (error) {
      console.error("Delete error:", error);
    }
  };
  
  // Handle drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handlePhotoUpload({ target: { files } });
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
       const [catRes, citiesRes, workersRes] = await Promise.all([
  axios.get(apiUrl("/services/categories")),
  axios.get(apiUrl("/cities")),
  axios.get(apiUrl("/workers?available_only=true"))
]);

        setCategories(catRes.data);
        setCities(citiesRes.data.cities);
        setWorkers(workersRes.data.workers);
        
        if (user?.id) {
         const requestsRes = await axios.get(apiUrl(`/customer/${user.id}/requests`));
          setMyRequests(requestsRes.data.requests);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.id]);

  const handleSubmitRequest = async () => {
    if (!selectedService || !city || !description || !user?.id) return;
    
    setSubmitting(true);
    try {
      // Build appointment datetime ISO string if date is set
      let appointmentDateTime = null;
      if (appointmentDate) {
        const time = appointmentTime || "09:00";
        appointmentDateTime = new Date(`${appointmentDate}T${time}`).toISOString();
      }
      
      await axios.post(apiUrl("/requests"), {
        client_id: user.id,
        service_id: selectedService,
        city: city,
        description: description,
        urgency: urgency,
        issue_photos: issuePhotos.map(p => p.id),
        appointment_date_time: appointmentDateTime
      });
      
      setSubmitSuccess(true);
      setSelectedService("");
      setDescription("");
      setIssuePhotos([]);
      setAppointmentDate("");
      setAppointmentTime("");
      
      const requestsRes = await axios.get(apiUrl(`/customer/${user.id}/requests`));
      setMyRequests(requestsRes.data.requests);
      
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (error) {
      console.error("Error submitting request:", error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="customer-dashboard">
      <div className="bg-blue-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1">Kunden-Dashboard</h1>
              <p className="text-blue-100">Willkommen, {user?.name || user?.phone || "Kunde"}</p>
            </div>
            <Badge className="bg-white/20 text-white border-white/30">
              <User className="h-3 w-3 mr-1" /> Kunde
            </Badge>
          </div>
        </div>
      </div>

      <div className="bg-white border-b sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1 overflow-x-auto">
            {[
              { id: "request", label: "Service anfragen", icon: FileText },
              { id: "workers", label: "Handwerker finden", icon: Users },
              { id: "orders", label: "Meine Aufträge", icon: ClipboardList }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-3 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === "request" && (
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-blue-600" />
                  Service anfragen
                </CardTitle>
                <CardDescription>Beschreiben Sie, was Sie benötigen</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {submitSuccess && (
                  <div className="p-4 bg-green-50 text-green-800 rounded-lg flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Anfrage erfolgreich gesendet!
                  </div>
                )}

                <div>
                  <Label>Service *</Label>
                  <Select value={selectedService} onValueChange={setSelectedService}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Service auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories && Object.entries(categories.categories).map(([groupKey, group]) => (
                        <div key={groupKey}>
                          <div className="px-2 py-1 text-sm font-semibold text-gray-500">{group.name_de}</div>
                          {group.services.map((service) => (
                            <SelectItem key={service.id} value={service.id}>
                              {service.name}
                            </SelectItem>
                          ))}
                        </div>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Stadt *</Label>
                  <Select value={city} onValueChange={setCity}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Stadt wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Beschreibung *</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Beschreiben Sie Ihr Problem oder Anliegen..."
                    className="mt-1 min-h-[120px]"
                  />
                </div>

                {/* NEW: Issue Photos Upload */}
                <div>
                  <Label className="flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" />
                    Fotos der Störung (optional, max. 5)
                  </Label>
                  <div 
                    className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => document.getElementById('photo-upload')?.click()}
                    data-testid="photo-upload-zone"
                  >
                    <input
                      id="photo-upload"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      className="hidden"
                      onChange={handlePhotoUpload}
                      disabled={uploadingPhotos || issuePhotos.length >= 5}
                    />
                    {uploadingPhotos ? (
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                        <p className="text-sm text-gray-600">Hochladen...</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">
                          Klicken oder Dateien hierher ziehen
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          JPG, PNG, WebP • Max. 5 MB pro Bild
                        </p>
                      </>
                    )}
                  </div>
                  
                  {photoError && (
                    <p className="text-sm text-red-600 mt-2">{photoError}</p>
                  )}
                  
                  {/* Photo Previews */}
                  {issuePhotos.length > 0 && (
                    <div className="mt-4 grid grid-cols-5 gap-2">
                      {issuePhotos.map((photo) => (
                        <div key={photo.id} className="relative group">
                          <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border">
                            <img 
                              src={photo.preview} 
                              alt={photo.filename}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removePhoto(photo.id); }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`remove-photo-${photo.id}`}
                          >
                            <XCircle className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* NEW: Appointment Date/Time - German only, calendar selection only */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Wunschtermin (optional)
                  </Label>
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-gray-500">Datum</Label>
                      <div className="relative mt-1">
                        <input
                          type="date"
                          value={appointmentDate}
                          onChange={(e) => {
                            const selectedDate = e.target.value;
                            const today = getMinDate();
                            // Validate: cannot select past dates
                            if (selectedDate >= today) {
                              setAppointmentDate(selectedDate);
                            }
                          }}
                          onKeyDown={(e) => e.preventDefault()}
                          min={getMinDate()}
                          lang="de"
                          className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                          style={{ colorScheme: 'light' }}
                          data-testid="appointment-date"
                        />
                      </div>
                      {appointmentDate && (
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(appointmentDate).toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Uhrzeit</Label>
                      <div className="relative mt-1">
                        <input
                          type="time"
                          value={appointmentTime}
                          onChange={(e) => {
                            const selectedTime = e.target.value;
                            // If date is today, validate time is not in past
                            if (appointmentDate === getMinDate()) {
                              const now = new Date();
                              const [hours, minutes] = selectedTime.split(':').map(Number);
                              if (hours < now.getHours() || (hours === now.getHours() && minutes < now.getMinutes())) {
                                return; // Don't allow past time
                              }
                            }
                            setAppointmentTime(selectedTime);
                          }}
                          onKeyDown={(e) => e.preventDefault()}
                          min={appointmentDate === getMinDate() ? getMinTime() : undefined}
                          lang="de"
                          className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                          style={{ colorScheme: 'light' }}
                          data-testid="appointment-time"
                        />
                      </div>
                      {appointmentTime && (
                        <p className="text-xs text-gray-500 mt-1">{appointmentTime} Uhr</p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Dringlichkeit</Label>
                  <Select value={urgency} onValueChange={setUrgency}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urgent">Dringend (heute/morgen)</SelectItem>
                      <SelectItem value="normal">Normal (diese Woche)</SelectItem>
                      <SelectItem value="flexible">Flexibel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 h-12"
                  disabled={!selectedService || !city || !description || submitting}
                  onClick={handleSubmitRequest}
                >
                  {submitting ? "Sende..." : "Anfrage senden"}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "workers" && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Verfügbare Handwerker</h2>
            {workers.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Noch keine Handwerker verfügbar</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {workers.map((worker) => (
                  <Card key={worker.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Avatar className="h-14 w-14">
                          <AvatarFallback className="bg-green-100 text-green-600 text-lg">
                            {(worker.name || "W").substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{worker.name || "Handwerker"}</h3>
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <MapPin className="h-3 w-3 mr-1" />
                            {worker.city || "Keine Stadt"}
                          </div>
                          {worker.rating > 0 && (
                            <div className="flex items-center text-sm text-amber-600 mt-1">
                              <Star className="h-3 w-3 mr-1 fill-current" />
                              {worker.rating}
                            </div>
                          )}
                        </div>
                      </div>
                      {worker.service_details && worker.service_details.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-1">
                          {worker.service_details.slice(0, 3).map((svc) => (
                            <Badge key={svc.id} variant="secondary" className="text-xs">
                              {svc.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "orders" && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Meine Aufträge</h2>
            {myRequests.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Noch keine Anfragen gestellt</p>
                  <Button 
                    className="mt-4" 
                    variant="outline"
                    onClick={() => setActiveTab("request")}
                  >
                    Erste Anfrage stellen
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {myRequests.map((request) => (
                  <Card key={request.id}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{request.service_name}</h3>
                          <p className="text-sm text-gray-500 mt-1">{request.description}</p>
                          <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                            <span className="flex items-center">
                              <MapPin className="h-3 w-3 mr-1" /> {request.city}
                            </span>
                            <span className="flex items-center">
                              <Calendar className="h-3 w-3 mr-1" /> 
                              {new Date(request.created_at).toLocaleDateString('de-DE')}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge className={
                            request.status === "open" ? "bg-blue-100 text-blue-800" :
                            request.status === "completed" ? "bg-green-100 text-green-800" :
                            "bg-gray-100 text-gray-800"
                          }>
                            {request.status === "open" ? "Offen" :
                             request.status === "completed" ? "Abgeschlossen" :
                             request.status}
                          </Badge>
                          
                          {/* Rating Button for completed orders */}
                          {request.status === "completed" && !request.rating_given && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                              onClick={() => {
                                setSelectedOrderForRating(request);
                                setRatingModalOpen(true);
                              }}
                            >
                              <Star className="h-4 w-4 mr-1" />
                              Bewerten
                            </Button>
                          )}
                          
                          {/* Already rated indicator */}
                          {request.rating_given && (
                            <span className="text-xs text-green-600 flex items-center">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Bewertet
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Rating Modal */}
        <RatingModal
          isOpen={ratingModalOpen}
          onClose={() => {
            setRatingModalOpen(false);
            setSelectedOrderForRating(null);
          }}
          order={selectedOrderForRating}
          onSubmit={async ({ rating, comment }) => {
            if (!selectedOrderForRating || !user?.id) return;
            
            await axios.post(apiUrl("/reviews") + `?customer_id=${user.id}`, {
              order_id: selectedOrderForRating.id,
              provider_id: selectedOrderForRating.assigned_provider_id || selectedOrderForRating.matched_providers?.[0],
              rating,
              comment
            });
            
            // Refresh requests to update rating_given status
            const requestsRes = await axios.get(apiUrl(`/customer/${user.id}/requests`));
            setMyRequests(requestsRes.data.requests || []);
          }}
        />
      </div>
    </div>
  );
};

// ==================== HANDWERKER (WORKER) DASHBOARD ====================
const HandwerkerDashboard = () => {
  const [activeTab, setActiveTab] = useState("requests");
  const [categories, setCategories] = useState(null);
  const [cities, setCities] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const user = getUser();
  const [name, setName] = useState(user?.name || "");
  const [city, setCity] = useState(user?.city || "");
  const [selectedServices, setSelectedServices] = useState(user?.services || []);
  const [workAreaKm, setWorkAreaKm] = useState(user?.work_area_km || 25);
  const [availability, setAvailability] = useState(user?.availability || "available");
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // NEW: Pricing state
  const [basePrice, setBasePrice] = useState(user?.pricing?.base_price || 0);
  const [hourlyRate, setHourlyRate] = useState(user?.pricing?.hourly_rate || 0);
  const [priceNotes, setPriceNotes] = useState(user?.pricing?.notes || "");
  
  // NEW: Availability info state
  const validStatuses = ["available", "busy"];
  const rawStatus = user?.availability_info?.status;
  const [availabilityCity, setAvailabilityCity] = useState(user?.availability_info?.city || user?.city || "");
  const [availabilityStatus, setAvailabilityStatus] = useState(
    validStatuses.includes(rawStatus) ? rawStatus : "available"
  );
  const [timeWindows, setTimeWindows] = useState(user?.availability_info?.time_windows || "Mo-Fr 09:00-18:00");
  
  // Safe status change handler
  const handleStatusChange = (newStatus) => {
    if (validStatuses.includes(newStatus)) {
      setAvailabilityStatus(newStatus);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Bump worker last_active_at (heartbeat — Smart-Match prefers active workers)
        const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
        if (token) {
          axios
            .post(apiUrl("/provider/heartbeat"), {}, { headers: { Authorization: `Bearer ${token}` } })
            .catch(() => {});
        }

        const [catRes, citiesRes] = await Promise.all([
          axios.get(apiUrl("/services/categories")),
          axios.get(apiUrl("/cities")),
        ]);
        setCategories(catRes.data);
        setCities(citiesRes.data.cities);
        
        if (user?.services && user.services.length > 0) {
          const requestsRes = await axios.get(apiUrl(`/workers/${user.id}/requests`));
          setIncomingRequests(requestsRes.data.requests);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user?.id, user?.services]);

  const toggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId)
        ? prev.filter(s => s !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;
    
    setSaving(true);
    try {
      const updateData = {};
      if (name) updateData.name = name;
      if (city) updateData.city = city;
      if (availability) updateData.availability = availability;
      if (workAreaKm) updateData.work_area_km = workAreaKm;
      if (selectedServices.length > 0) updateData.services = selectedServices;
      
      // NEW: Include pricing data
      updateData.pricing = {
        base_price: basePrice,
        hourly_rate: hourlyRate,
        notes: priceNotes
      };
      
      // NEW: Include availability info
      updateData.availability_info = {
        city: availabilityCity || city,
        status: availabilityStatus,
        time_windows: timeWindows
      };
      
      const response = await axios.put(apiUrl(`/user/${user.id}`), updateData);
      
      if (response.data.user) {
        saveAuth(response.data.user);
      }
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
      if (selectedServices.length > 0) {
        const requestsRes = await axios.get(apiUrl(`/workers/${user.id}/requests`));
        setIncomingRequests(requestsRes.data.requests);
      }
    } catch (error) {
      console.error("Error saving profile:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="handwerker-dashboard">
      <div className="bg-green-600 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1">Handwerker-Dashboard</h1>
              <p className="text-green-100">Willkommen, {user?.name || user?.phone || "Handwerker"}</p>
            </div>
            <Badge className="bg-white/20 text-white border-white/30">
              <Wrench className="h-3 w-3 mr-1" /> Handwerker
            </Badge>
          </div>
        </div>
      </div>

      <div className="bg-white border-b sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1 overflow-x-auto">
            {[
              { id: "requests", label: "Anfragen", icon: ClipboardList },
              { id: "services", label: "Services", icon: Briefcase },
              { id: "prices", label: "Preise", icon: DollarSign },
              { id: "availability", label: "Verfügbarkeit", icon: Clock },
              { id: "reviews", label: "Bewertungen", icon: Star },
              { id: "profile", label: "Profil", icon: UserCheck }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-3 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-green-600 text-green-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === "requests" && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Eingehende Anfragen</h2>
            
            {!selectedServices || selectedServices.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">Konfigurieren Sie zuerst Ihre Services</p>
                  <Button onClick={() => setActiveTab("services")}>
                    Services einrichten
                  </Button>
                </CardContent>
              </Card>
            ) : incomingRequests.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Noch keine passenden Anfragen</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {incomingRequests.map((request) => {
                  const isNew = request.status === "new" || !request.assigned_provider_id;
                  const ageMinutes = request.created_at
                    ? Math.max(0, Math.floor((Date.now() - new Date(request.created_at).getTime()) / 60000))
                    : null;
                  return (
                  <Card
                    key={request.id}
                    data-testid={isNew ? "lead-card-new" : "lead-card"}
                    className={`hover:shadow-lg transition-shadow ${
                      isNew ? "border-2 border-red-400 ring-2 ring-red-200/60 animate-[pulseRing_2s_ease-in-out_infinite]" : ""
                    }`}
                  >
                    <CardContent className="p-6">
                      {isNew && (
                        <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-red-700 bg-red-50 px-3 py-1.5 rounded-full w-fit">
                          <span className="h-2 w-2 bg-red-500 rounded-full animate-pulse" />
                          Neue Anfrage – jetzt antworten!
                          <span className="text-red-600 font-normal ml-1">
                            Empfohlen: innerhalb von 5 Minuten
                          </span>
                        </div>
                      )}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-gray-900">{request.service_name}</h3>
                            <Badge className={
                              request.urgency === "urgent" ? "bg-red-100 text-red-800" :
                              request.urgency === "normal" ? "bg-blue-100 text-blue-800" :
                              "bg-gray-100 text-gray-800"
                            }>
                              {request.urgency === "urgent" ? "Dringend" :
                               request.urgency === "normal" ? "Normal" : "Flexibel"}
                            </Badge>
                          </div>
                          <p className="text-gray-600 mb-3">{request.description}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                            <span className="flex items-center">
                              <MapPin className="h-3 w-3 mr-1" /> {request.city}
                            </span>
                            <span className="flex items-center">
                              <Calendar className="h-3 w-3 mr-1" /> 
                              {new Date(request.created_at).toLocaleDateString('de-DE')}
                            </span>
                            {ageMinutes !== null && (
                              <span className="flex items-center text-gray-600">
                                <Clock className="h-3 w-3 mr-1" />
                                {ageMinutes < 1 ? "Gerade eben" :
                                 ageMinutes < 60 ? `Vor ${ageMinutes} Min.` :
                                 `Vor ${Math.floor(ageMinutes / 60)} Std.`}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          className={isNew ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}
                          data-testid={isNew ? "lead-accept-btn-urgent" : "lead-accept-btn"}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          {isNew ? "Annehmen" : "Antworten"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === "services" && (
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Briefcase className="h-5 w-5 mr-2 text-green-600" />
                  Services verwalten
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {saveSuccess && (
                  <div className="p-4 bg-green-50 text-green-800 rounded-lg flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Erfolgreich gespeichert!
                  </div>
                )}

                {selectedServices.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 bg-green-50 rounded-lg">
                    <span className="text-sm text-green-700 font-medium">Ausgewählt:</span>
                    {selectedServices.map(sid => {
                      let svc = null;
                      if (categories) {
                        for (const group of Object.values(categories.categories)) {
                          svc = group.services.find(s => s.id === sid);
                          if (svc) break;
                        }
                      }
                      return svc ? (
                        <Badge key={sid} className="bg-green-600">
                          {svc.name}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}

                <ScrollArea className="h-80 border rounded-lg p-4">
                  {categories && Object.entries(categories.categories).map(([groupKey, group]) => (
                    <div key={groupKey} className="mb-6">
                      <h4 className="font-semibold text-gray-800 mb-3">{group.name_de}</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {group.services.map((service) => {
                          const Icon = SERVICE_ICONS[service.id] || Wrench;
                          const isSelected = selectedServices.includes(service.id);
                          return (
                            <button
                              key={service.id}
                              onClick={() => toggleService(service.id)}
                              className={`flex items-center p-3 rounded-lg border-2 transition-all text-left ${
                                isSelected 
                                  ? "border-green-500 bg-green-50 text-green-800" 
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              <Icon className={`h-4 w-4 mr-2 ${isSelected ? "text-green-600" : "text-gray-400"}`} />
                              <span className="text-sm">{service.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </ScrollArea>

                <div>
                  <Label>Arbeitsradius: {workAreaKm} km</Label>
                  <input
                    type="range"
                    min="5"
                    max="100"
                    value={workAreaKm}
                    onChange={(e) => setWorkAreaKm(parseInt(e.target.value))}
                    className="w-full mt-2"
                  />
                </div>

                <Button 
                  className="w-full bg-green-600 hover:bg-green-700 h-12"
                  onClick={handleSaveProfile}
                  disabled={saving}
                >
                  {saving ? "Speichere..." : "Services speichern"}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "prices" && (
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                  Preise festlegen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {selectedServices.length === 0 ? (
                  <div className="text-center py-8">
                    <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Wählen Sie zuerst Ihre Services</p>
                    <Button className="mt-4" variant="outline" onClick={() => setActiveTab("services")}>
                      Services einrichten
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {saveSuccess && (
                      <div className="p-4 bg-green-50 text-green-800 rounded-lg flex items-center">
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Preise gespeichert!
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Grundpreis (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={basePrice}
                          onChange={(e) => setBasePrice(parseFloat(e.target.value) || 0)}
                          placeholder="z.B. 50"
                          className="mt-1"
                          data-testid="base-price-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Anfahrtspauschale / Mindestpreis</p>
                      </div>
                      <div>
                        <Label>Stundensatz (€)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={hourlyRate}
                          onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
                          placeholder="z.B. 65"
                          className="mt-1"
                          data-testid="hourly-rate-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Pro Arbeitsstunde</p>
                      </div>
                    </div>
                    
                    <div>
                      <Label>Preishinweise</Label>
                      <Textarea
                        value={priceNotes}
                        onChange={(e) => setPriceNotes(e.target.value)}
                        placeholder="z.B. Material wird separat berechnet, Wochenendaufschlag 20%..."
                        className="mt-1"
                        data-testid="price-notes-input"
                      />
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <h4 className="font-medium text-gray-700 mb-3">Ihre Services</h4>
                      <div className="space-y-2">
                        {selectedServices.map(sid => {
                          let svc = null;
                          if (categories) {
                            for (const group of Object.values(categories.categories)) {
                              svc = group.services.find(s => s.id === sid);
                              if (svc) break;
                            }
                          }
                          return svc ? (
                            <div key={sid} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="font-medium">{svc.name}</span>
                              <span className="text-green-600">
                                {basePrice > 0 || hourlyRate > 0 
                                  ? `ab ${basePrice}€ + ${hourlyRate}€/Std`
                                  : "Preis auf Anfrage"}
                              </span>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                    
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700 h-12"
                      onClick={handleSaveProfile}
                      disabled={saving}
                    >
                      {saving ? "Speichere..." : "Preise speichern"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "availability" && (
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-green-600" />
                  Verfügbarkeit einstellen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {saveSuccess && (
                  <div className="p-4 bg-green-50 text-green-800 rounded-lg flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Verfügbarkeit gespeichert!
                  </div>
                )}
                
                <div>
                  <Label>Stadt / Einsatzgebiet</Label>
                  <Select value={availabilityCity} onValueChange={setAvailabilityCity}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Stadt wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Status</Label>
                  {/* Using native select for stability */}
                  <select
                    value={availabilityStatus}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="mt-1 w-full h-10 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    data-testid="availability-status-select"
                  >
                    <option value="available">🟢 Verfügbar</option>
                    <option value="busy">🔴 Beschäftigt</option>
                  </select>
                </div>
                
                <div>
                  <Label>Arbeitszeiten (optional)</Label>
                  <Input
                    value={timeWindows}
                    onChange={(e) => setTimeWindows(e.target.value)}
                    placeholder="z.B. Mo-Fr 09:00-18:00"
                    className="mt-1"
                    data-testid="time-windows-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Geben Sie Ihre üblichen Arbeitszeiten an
                  </p>
                </div>
                
                <div>
                  <Label>Arbeitsradius: {workAreaKm} km</Label>
                  <input
                    type="range"
                    min="5"
                    max="100"
                    value={workAreaKm}
                    onChange={(e) => setWorkAreaKm(parseInt(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5 km</span>
                    <span>100 km</span>
                  </div>
                </div>
                
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700 h-12"
                  onClick={handleSaveProfile}
                  disabled={saving}
                >
                  {saving ? "Speichere..." : "Verfügbarkeit speichern"}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "reviews" && (
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Star className="h-5 w-5 mr-2 text-yellow-500" />
                  Meine Bewertungen
                </CardTitle>
                <CardDescription>
                  Sehen Sie, was Ihre Kunden über Sie sagen
                </CardDescription>
              </CardHeader>
              <CardContent>
                {user?.id && <ReviewsList providerId={user.id} />}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "profile" && (
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <UserCheck className="h-5 w-5 mr-2 text-green-600" />
                  Profil bearbeiten
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {saveSuccess && (
                  <div className="p-4 bg-green-50 text-green-800 rounded-lg flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Profil gespeichert!
                  </div>
                )}

                <div>
                  <Label>Name / Firmenname</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ihr Name oder Firmenname"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Telefonnummer</Label>
                  <Input
                    value={user?.phone || ""}
                    disabled
                    className="mt-1 bg-gray-50"
                  />
                </div>

                <div>
                  <Label>Stadt</Label>
                  <Select value={city} onValueChange={setCity}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Stadt wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Verfügbarkeit</Label>
                  <Select value={availability} onValueChange={setAvailability}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Verfügbar</SelectItem>
                      <SelectItem value="available_today">Heute verfügbar</SelectItem>
                      <SelectItem value="available_week">Diese Woche verfügbar</SelectItem>
                      <SelectItem value="busy">Beschäftigt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  className="w-full bg-green-600 hover:bg-green-700 h-12"
                  onClick={handleSaveProfile}
                  disabled={saving}
                >
                  {saving ? "Speichere..." : "Profil speichern"}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== IMPORTS FOR NEW PAGES ====================
import MobileBottomNav from "./components/MobileBottomNav";
import RequestServicePage from "./pages/RequestServicePage";
import ProviderRegisterPage from "./pages/ProviderRegisterPage";

// ==================== MAIN APP ====================
// Wrapper that reads auth state and supplies it to NotificationsProvider so
// the bell + polling react instantly to login/logout.
function AuthAwareNotifications({ children }) {
  const { user } = useAuth();
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const role = user?.role || (typeof window !== "undefined" ? localStorage.getItem("role") : null);
  return (
    <NotificationsProvider token={token} role={role}>
      {children}
    </NotificationsProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AuthAwareNotifications>
          <div className="min-h-screen flex flex-col">
            <Header />
          
          <main className="flex-1 pb-16 md:pb-0">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/login" element={
                <GuestOnlyRoute>
                  <LoginPage />
                </GuestOnlyRoute>
              } />
              <Route path="/register" element={
                <GuestOnlyRoute>
                  <RegisterPage />
                </GuestOnlyRoute>
              } />
              
              {/* Customer Request Flow - guests + customers only (workers blocked) */}
              <Route path="/request-service" element={
                <GuestOrCustomerRoute>
                  <RequestServicePage />
                </GuestOrCustomerRoute>
              } />
              
              {/* Provider Registration Flow - guests only */}
              <Route path="/provider/register" element={
                <GuestOrProviderRoute>
                  <ProviderRegisterPage />
                </GuestOrProviderRoute>
              } />
              
              {/* Customer Dashboard - Protected (customer only) */}
              <Route path="/customer" element={
                <CustomerOnlyRoute>
                  <CustomerDashboard />
                </CustomerOnlyRoute>
              } />
              <Route path="/customer/dashboard" element={
                <CustomerOnlyRoute>
                  <CustomerDashboard />
                </CustomerOnlyRoute>
              } />
              
              {/* Provider/Handwerker Dashboard - Protected (worker only) */}
              <Route path="/handwerker" element={
                <ProviderOnlyRoute>
                  <HandwerkerDashboard />
                </ProviderOnlyRoute>
              } />
              <Route path="/provider/dashboard" element={
                <ProviderOnlyRoute>
                  <HandwerkerDashboard />
                </ProviderOnlyRoute>
              } />
              
              {/* Legacy routes redirect */}
              <Route path="/worker/dashboard" element={<Navigate to="/handwerker" replace />} />
              <Route path="/dashboard" element={<Navigate to="/login" replace />} />
              
              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          
          {/* Mobile Bottom Navigation */}
          <MobileBottomNav />
          
          <div className="hidden md:block">
            <Footer />
          </div>

          {/* Global toast notifications */}
          <Toaster position="top-right" richColors closeButton />
        </div>
        </AuthAwareNotifications>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
